import {AnyAction, countPatchChanges, isDocumentAction, UnknownError} from "@buildwithflux/core";
import {areWeInStorybook} from "@buildwithflux/shared";
import {revertChange} from "deep-diff";
import type {FluxServices} from "export";
import {create} from "mutative";
import {EMPTY, of, Subject, Subscription} from "rxjs";
import {catchError, delay, filter, map, mergeMap, takeUntil, tap} from "rxjs/operators";

import {DocumentStatusStates} from "modules/app/state/app";
import type {AppStore} from "modules/app/state/app";
import type {CurrentUserService} from "modules/auth";
import type {UseAuthPermissionsStore} from "modules/auth/state/permissions";
import {
    createProjectDoc,
    deleteProjectDoc,
    updateProjectDoc,
    writeDocument,
} from "modules/document/state/actionCreators/documentActions";
import type {DocumentStore} from "modules/document/state/document";
import {isBatchAction} from "modules/document/state/helpers/batchActions";
import {useFeatureFlags} from "modules/feature_flags/hooks";
import {queueActionRecord as queueActionRecordWrite} from "modules/storage_engine/common/ActionRecordHelpers";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * Service that handles action record generation.
 *
 * This service replaces the Redux epic `makeGenerateActionRecordsEpic` using a hybrid approach:
 * it uses RxJS internally for action stream processing while fitting into the FluxServices DI pattern.
 *
 * The service subscribes to an action stream and generates action records when
 * document actions with `shouldGenerateActionRecord` flag are dispatched.
 *
 * Key behaviors:
 * - Filters for document actions with shouldGenerateActionRecord flag
 * - Filters out actions with empty patches
 * - Sets "Pending changes..." status immediately (actual "Saving..." is set in DocumentSaveService after debounce)
 * - Undoes any active preview before generating action record
 * - Delays 200ms to move after rendering
 * - Checks user permissions (edit or commenting)
 * - Queues action record write
 * - Dispatches writeDocument action if user has edit permission
 */
export interface ActionRecordService {
    /**
     * Called when an action is dispatched.
     * The service filters and processes relevant actions internally.
     */
    onAction: (action: AnyAction) => void;

    /**
     * Disposes of all subscriptions and cleans up resources.
     * Should be called when the service is no longer needed.
     */
    dispose: () => void;
}

export interface ActionRecordServiceDependencies {
    appStore: AppStore;
    currentUserService: CurrentUserService;
    /**
     * The Zustand store for auth permissions
     */
    useAuthPermissionsStore: UseAuthPermissionsStore;
    /**
     * The full FluxServices container, needed for queueActionRecordWrite
     */
    services: FluxServices;
    /**
     * The Zustand document store for reading document state.
     */
    documentStore: DocumentStore;
}

/**
 * Checks if an action should generate an action record.
 * For batch actions, checks if ANY sub-action has shouldGenerateActionRecord: true.
 * For non-batch actions, checks the action directly.
 */
function shouldGenerateActionRecord(action: AnyAction): boolean {
    if (isBatchAction(action)) {
        // For batch actions, check if any sub-action has shouldGenerateActionRecord
        return action.payload.some(
            (subAction: AnyAction) => isDocumentAction(subAction) && !!subAction.payload?.shouldGenerateActionRecord,
        );
    }
    // For non-batch actions, check the action directly
    return isDocumentAction(action) && !!action.payload?.shouldGenerateActionRecord;
}

/**
 * Creates an ActionRecordService instance.
 *
 * This factory function creates a service that handles action record generation
 * using RxJS for action stream processing.
 *
 * @param dependencies - The service dependencies
 * @returns An ActionRecordService instance
 */
export function createActionRecordService(dependencies: ActionRecordServiceDependencies): ActionRecordService {
    const {appStore, currentUserService, useAuthPermissionsStore, services, documentStore} = dependencies;
    const action$ = new Subject<AnyAction>();
    const destroy$ = new Subject<void>();
    const subscriptions: Subscription[] = [];
    let disposed = false;

    // Determine the status to set based on feature flags
    const getStatus = () =>
        useFeatureFlags.getState().disableAutoSave ?? false
            ? DocumentStatusStates.syncNeeded
            : DocumentStatusStates.pending;

    // Action record generation subscription
    // Handles generating action records for document changes
    const actionRecordSubscription = action$
        .pipe(
            takeUntil(destroy$),
            // Filter for document actions with shouldGenerateActionRecord flag
            // For batch actions, checks if any sub-action has shouldGenerateActionRecord: true
            filter((action: AnyAction) => shouldGenerateActionRecord(action) && !areWeInStorybook()),
            // Get the current state and pair with action
            map((action: AnyAction) => {
                const documentState = services.documentStore.getState().document;
                const isUpdateMarkdownDoc =
                    action.type === updateProjectDoc.type ||
                    action.type === createProjectDoc.type ||
                    action.type === deleteProjectDoc.type;
                return {action, documentState, isUpdateMarkdownDoc};
            }),
            // Filter out actions with empty patches
            filter(({documentState, isUpdateMarkdownDoc}) => {
                if (isUpdateMarkdownDoc) {
                    return true;
                }
                const latestPatch = documentState?.latestPatch;
                const latestPatchSize = latestPatch ? countPatchChanges(latestPatch) : 0;
                return latestPatchSize > 0;
            }),
            // Set the "Pending changes..." status (actual "Saving..." is set in DocumentSaveService after debounce)
            tap(({documentState}) => {
                if (documentState) {
                    const timestamp = new Date().getTime();
                    const status = getStatus();
                    // Update Zustand app store directly (no Redux dispatch needed)
                    appStore.getState().setDocumentStatus(status, timestamp);
                }
            }),
            // Undo any active preview first, if any
            map(({action, documentState}) => {
                if (!documentState) {
                    return {action, documentState};
                }

                const depreviewedDocumentState = create(documentState, (draft) => {
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    draft.preview?.diff.forEach((change) => {
                        revertChange(draft, documentState, change);
                    });

                    delete draft.preview;
                });

                return {action, documentState: depreviewedDocumentState};
            }),
            // 200ms delay is the time needed to move this after rendering, according to testing
            delay(200),
            // Process the action and generate action record
            mergeMap(({action, documentState: newDocumentState}) => {
                // Get auth permissions from Zustand store instead of Redux state
                const {currentUserHasEditPermission, currentUserHasCommentingPermission} =
                    useAuthPermissionsStore.getState();
                const currentUser = currentUserService.getCurrentUser();

                try {
                    if (!currentUser) {
                        throw new Error(`No current user!`);
                    }
                    if (!newDocumentState) {
                        throw new Error(`No document state!`);
                    }

                    // User must have at least commenting permission to write action records
                    if (!(currentUserHasEditPermission || currentUserHasCommentingPermission)) {
                        throw new Error(`User ${currentUser.handle} does not have "edit" or "commenting" permissions`);
                    }

                    // NOTE: useful for debugging hard to reproduce issues
                    if (useFeatureFlags.getState().disableActionRecordWrites ?? false) {
                        const syncedTimestamp = Date.now();
                        // Update Zustand app store directly (no Redux dispatch needed)
                        appStore.getState().setDocumentStatus(DocumentStatusStates.synced, syncedTimestamp);
                        return EMPTY;
                    }

                    queueActionRecordWrite(currentUser, action, newDocumentState, services);

                    // QUESTION: what if a user has commenting permission but not edit permission? no save?
                    if (currentUserHasEditPermission) {
                        // writeDocument still needs to go through Redux because DocumentSaveService
                        // listens for it via the action stream
                        return of(writeDocument(newDocumentState));
                    }
                    const commentOnlySyncedTimestamp = Date.now();
                    // Update Zustand app store directly (no Redux dispatch needed)
                    appStore.getState().setDocumentStatus(DocumentStatusStates.synced, commentOnlySyncedTimestamp);
                    return EMPTY;
                } catch (error) {
                    if (error instanceof Error) {
                        error.message = `Error trying to generate an action record from action ${action.type}: ${error.message}`;
                        error.cause = error;
                    }
                    FluxLogger.captureError(error);

                    // QUESTION: is it always a good idea to only attempt the
                    // save if the action record succeeds?
                    const errorTimestamp = new Date().getTime();
                    // Update Zustand app store directly (no Redux dispatch needed)
                    appStore.getState().setDocumentStatus(DocumentStatusStates.error, errorTimestamp, "Failed to save");
                    return EMPTY;
                }
            }),
            // Top-level catchError to prevent any unexpected errors from crashing the process
            catchError((error) => {
                FluxLogger.captureError(
                    new UnknownError(`Unexpected error in action record stream: ${JSON.stringify(error)}`, error),
                );
                return EMPTY;
            }),
        )
        .subscribe({
            next: (action) => {
                // writeDocument is a signal action not processed by the document store's
                // action handler chain. Send it directly to DocumentSaveService.
                services.documentSaveService.onAction(action);
            },
            error: (error) => {
                FluxLogger.captureError(
                    new UnknownError(`Action record subscription error: ${JSON.stringify(error)}`, error),
                );
            },
        });
    subscriptions.push(actionRecordSubscription);

    const service: ActionRecordService = {
        onAction: (action: AnyAction) => {
            if (disposed) {
                return;
            }
            action$.next(action);
        },
        dispose: () => {
            disposed = true;
            destroy$.next();
            destroy$.complete();
            for (const subscription of subscriptions) {
                subscription.unsubscribe();
            }
            action$.complete();
        },
    };

    return service;
}
